// Internet ADS-B traffic poller. Talks to our edge proxy at
// /api/traffic/bbox which fronts adsb.fi (or a future commercial
// upstream). Pushes normalized targets into useTrafficStore.
//
// Polling cadence:
//   foreground + map page + recent user activity:  5 s
//   foreground + parked / taxiing (gs < 5 kt):    15 s
//   backgrounded tab (visibilityState hidden):    30 s
//
// Auto-disable at altitude: when the user has the "auto-disable in
// flight" preference on (default), the poller stops requesting new
// data above 5,000 ft GPS-AGL. Stale internet traffic at altitude is
// worse than no traffic — see docs/adsb-integration-plan.md § Wave 1.
//
// The poller does not own UI state; it only reads ownship position
// from useGpsStore and writes targets / status into useTrafficStore.

import { useGpsStore } from '../../../stores/gpsStore';
import { useTrafficStore } from '../store';
import { getMapInstance } from '../../../map/MapView';
import type { TrafficTarget } from '../types';

interface PollerSettings {
  /** Search radius in nm. */
  rangeNm: number;
  /** Auto-disable when ownship GPS altitude > thresholdFt above sea
   *  level. Internet feeds go stale fast past cell coverage. */
  autoDisableAboveFt: number;
  /** When false, poller never starts. */
  enabled: boolean;
}

const DEFAULT_SETTINGS: PollerSettings = {
  rangeNm: 40,
  autoDisableAboveFt: 5000,
  enabled: false,
};

const FOREGROUND_INTERVAL_MS = 5_000;
const SLOW_INTERVAL_MS = 15_000;
const BACKGROUND_INTERVAL_MS = 30_000;
// Per-poll hard timeout: shorter than the fastest polling cadence so a
// hung request never blocks the next poll and traffic icons don't freeze.
const POLL_TIMEOUT_MS = 4_000;

let _settings: PollerSettings = { ...DEFAULT_SETTINGS };
let _timer: ReturnType<typeof setTimeout> | null = null;
let _inFlight: AbortController | null = null;
let _disposed = false;

/**
 * Update settings live. Restarts the poll loop on the next tick so a
 * range change applies immediately rather than at the next interval.
 */
export function updateInternetTrafficSettings(patch: Partial<PollerSettings>): void {
  _settings = { ..._settings, ...patch };
  if (_settings.enabled && !_disposed) {
    schedule(0);
  } else {
    cancelTimer();
    // Don't clear the store here — the UI may still want to show the
    // last-known targets; gc() will sweep them once they age out.
  }
}

export function getInternetTrafficSettings(): PollerSettings {
  return { ..._settings };
}

/** Mount the poller. Safe to call multiple times; subsequent calls
 *  no-op until disposeInternetTraffic() is called first. */
export function startInternetTraffic(initial?: Partial<PollerSettings>): void {
  if (_timer != null || _disposed) return;
  _settings = { ...DEFAULT_SETTINGS, ..._settings, ...initial };
  // Schedule the first poll immediately when enabled is true.
  schedule(_settings.enabled ? 0 : FOREGROUND_INTERVAL_MS);
}

export function disposeInternetTraffic(): void {
  _disposed = true;
  cancelTimer();
  if (_inFlight) {
    _inFlight.abort();
    _inFlight = null;
  }
}

function cancelTimer(): void {
  if (_timer != null) {
    clearTimeout(_timer);
    _timer = null;
  }
}

function schedule(inMs: number): void {
  cancelTimer();
  if (_disposed) return;
  _timer = setTimeout(() => { void pollOnce(); }, inMs);
}

async function pollOnce(): Promise<void> {
  if (_disposed) return;
  if (!_settings.enabled) {
    schedule(FOREGROUND_INTERVAL_MS);
    return;
  }

  // Query origin: prefer GPS fix. Fall back to the map's current
  // center when GPS isn't available (user hasn't granted permission,
  // browser-only viewing, etc.) so the pilot still sees traffic for
  // wherever they're looking on the map.
  const fix = useGpsStore.getState().fix;
  let originLat: number;
  let originLon: number;
  let altMeters: number | null = null;
  let speedMps: number | null = null;
  if (fix) {
    originLat = fix.lat;
    originLon = fix.lon;
    altMeters = fix.altMeters ?? null;
    speedMps = fix.speedMps ?? null;
  } else {
    const map = getMapInstance();
    if (!map) {
      // No GPS AND no map yet. Try again soon.
      schedule(FOREGROUND_INTERVAL_MS);
      return;
    }
    const c = map.getCenter();
    originLat = c.lat;
    originLon = c.lng;
  }

  // Auto-disable above the altitude threshold (cell coverage degrades
  // fast above ~5000 AGL outside metros; stale traffic is dangerous).
  // Only meaningful when we have an actual GPS-altitude reference.
  if (_settings.autoDisableAboveFt > 0 && altMeters != null) {
    const altFt = altMeters * 3.28084;
    if (altFt > _settings.autoDisableAboveFt) {
      useTrafficStore
        .getState()
        .reportSourceError(
          'internet',
          `Disabled above ${_settings.autoDisableAboveFt} ft (no cell coverage).`,
        );
      schedule(FOREGROUND_INTERVAL_MS);
      return;
    }
  }

  // Slow down when stationary (parked / taxiing). At 5 kt or less,
  // traffic concerns are slower-changing too.
  const slow = speedMps != null && speedMps * 1.94384 < 5;
  const interval = slow ? SLOW_INTERVAL_MS
    : (typeof document !== 'undefined' && document.visibilityState === 'hidden')
      ? BACKGROUND_INTERVAL_MS
      : FOREGROUND_INTERVAL_MS;

  // Issue the request. Abort the previous one if any (defensive — the
  // schedule loop is serial, but a settings change can reset it
  // mid-flight).
  _inFlight?.abort();
  const ac = new AbortController();
  _inFlight = ac;

  const qs = new URLSearchParams({
    lat: originLat.toFixed(4),
    lon: originLon.toFixed(4),
    dist: String(_settings.rangeNm),
  });

  // Race the fetch against a hard per-poll timeout. If the timeout wins,
  // the AbortController fires, the catch branch marks the source degraded,
  // and the next poll is scheduled immediately rather than waiting.
  const pollTimeout = setTimeout(() => ac.abort(), POLL_TIMEOUT_MS);
  try {
    const r = await fetch(`/api/traffic/bbox?${qs.toString()}`, {
      signal: ac.signal,
      headers: { 'accept': 'application/json' },
    });
    if (!r.ok) {
      const text = await r.text().catch(() => '');
      useTrafficStore
        .getState()
        .reportSourceError(
          'internet',
          `HTTP ${r.status}${text ? ' — ' + text.slice(0, 120) : ''}`,
        );
    } else {
      const json = (await r.json()) as { targets?: TrafficTarget[] };
      const targets = Array.isArray(json.targets) ? json.targets : [];
      useTrafficStore.getState().pushTargets('internet', targets);
    }
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError' && _inFlight !== ac) {
      // Cancelled by settings change (we already replaced _inFlight).
      // Don't report as an error or schedule — the new settings will.
      clearTimeout(pollTimeout);
      return;
    } else if ((err as { name?: string }).name === 'AbortError') {
      // Poll timeout — mark source degraded so the UI can show stale warning.
      useTrafficStore.getState().reportSourceError('internet', 'poll timed out');
    } else {
      useTrafficStore
        .getState()
        .reportSourceError(
          'internet',
          err instanceof Error ? err.message : String(err),
        );
    }
  } finally {
    clearTimeout(pollTimeout);
    if (_inFlight === ac) _inFlight = null;
  }

  schedule(interval);
}
